import React, { useState, useEffect, useCallback } from 'react';
import { Form } from 'react-bootstrap';
import { deepGet } from '../utils/dynamicFormUtils';
import { fetchDynamicSelectOptions } from '../api/payoutMethodApi';
import SelectMulti from '../../../../../../../components/SelectMulti';
import Radio from '../../../../../../../components/radioButton/radio';

/** Gợi ý placeholder từ pattern thường gặp (để dễ nhập). */
function getPlaceholderFromPattern(pattern) {
  if (!pattern || typeof pattern !== 'string') return '';
  const p = pattern.trim();
  if (!p) return '';
  // Số (routing, account...)
  if (/\\d|\[0-9\]|\d+/.test(p) && !/[a-zA-Z]/.test(p)) return 'e.g. 000000000';
  // Chữ in hoa 2 ký tự (country code)
  if (/\[A-Z\]|\\u|[A-Z]{2}/.test(p) || /country|code/i.test(p)) return 'e.g. US';
  // Email
  if (/@|email|mail/i.test(p)) return 'e.g. name@example.com';
  // Chuỗi chữ
  if (/[a-zA-Z]/.test(p)) return 'e.g. text';
  return '';
}

/**
 * @param {{
 *   fieldSchema: import('../utils/fieldSchemaTypes').FieldSchema,
 *   value: *,
 *   error?: string,
 *   formData: Object,
 *   onChange: (value: *) => void,
 *   disabled?: boolean,
 * }}
 */
function DynamicField({ fieldSchema, value, error, formData, onChange, disabled }) {
  const { path, required, field, rule } = fieldSchema || {};
  const type = field?.type || 'INPUT';
  const label = field?.label ?? path;
  const placeholder =
    field?.placeholder ||
    field?.example ||
    (type === 'INPUT' ? getPlaceholderFromPattern(rule?.pattern) : '') ||
    '';
  const options = field?.options ?? [];

  const [dynamicOptions, setDynamicOptions] = useState([]);
  const [dynamicLoading, setDynamicLoading] = useState(false);

  const loadDynamicOptions = useCallback(async () => {
    const dyn = field?.dynamic_options;
    if (!dyn || type !== 'DYNAMIC_SELECT') return;
    setDynamicLoading(true);
    try {
      const list = await fetchDynamicSelectOptions(dyn, formData, deepGet);
      setDynamicOptions(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error('fetchDynamicSelectOptions failed', err);
      setDynamicOptions([]);
    } finally {
      setDynamicLoading(false);
    }
  }, [field?.dynamic_options, formData, type]);

  useEffect(() => {
    // DYNAMIC_SELECT tạm render thành INPUT, không gọi API options
    if (type === 'DYNAMIC_SELECT' || !field?.dynamic_options) return;
    loadDynamicOptions();
  }, [type, field?.dynamic_options, formData]);

  const displayValue = value !== undefined && value !== null ? value : '';
  const hasError = Boolean(error);
  const commonProps = {
    disabled: disabled || dynamicLoading,
    'aria-invalid': hasError,
  };

  const renderInput = () => (
    <Form.Control
      type="text"
      value={displayValue}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className='form-custom'
      {...commonProps}
    />
  );

  const renderSelect = () => {
    const selectOptions = type === 'DYNAMIC_SELECT' ? dynamicOptions : options;
    const normalizedValue = displayValue 
      ? selectOptions.find(opt => opt.value === displayValue) || null
      : null;

    return (
      <SelectMulti
        value={normalizedValue}
        onChange={(selectedOption) => {
          onChange(selectedOption ? selectedOption.value : '');
        }}
        options={selectOptions}
        isMulti={false}
        placeholder={placeholder || 'Select...'}
        error={hasError}
        disabled={commonProps.disabled}
      />
    );
  };

  const renderRadio = () => (
    <div className="d-flex flex-wrap gap-3">
      {(field?.options || []).map((opt) => (
        <Radio
          key={opt.value}
          id={`${path}-${opt.value}`}
          name={path}
          text={opt.label}
          value={opt.value}
          checked={String(displayValue) === String(opt.value)}
          onChange={() => onChange(opt.value)}
          disabled={disabled || dynamicLoading}
        />
      ))}
    </div>
  );

  const renderTransferMethod = () => (
    <div className="d-flex flex-column gap-2">
      {(field?.options || []).map((opt) => (
        <Form.Check
          key={opt.value}
          type="radio"
          id={`${path}-${opt.value}`}
          name={path}
          value={opt.value}
          checked={String(displayValue) === String(opt.value)}
          onChange={() => onChange(opt.value)}
          disabled={disabled}
          label={
            <span>
              <strong>{opt.label}</strong>
              {opt.description && (
                <small className="d-block text-muted">{opt.description}</small>
              )}
            </span>
          }
        />
      ))}
    </div>
  );

  const renderControl = () => {
    switch (type) {
      case 'SELECT':
        return renderSelect();
      case 'RADIO':
        return renderRadio();
      case 'DYNAMIC_SELECT':
        // Tạm dùng INPUT để user tự nhập; dynamic options hỗ trợ sau
        return renderInput();
      case 'TRANSFER_METHOD':
        return renderTransferMethod();
      default:
        return renderInput();
    }
  };

  if (!fieldSchema || !fieldSchema.enabled) return null;

  return (
    <Form.Group className={`mb-3 ${hasError ? 'has-error' : ''}`} controlId={path}>
      <Form.Label>
        {label}
        {required && <span className="text-danger ms-1">*</span>}
      </Form.Label>
      {renderControl()}
      {hasError && <Form.Control.Feedback type="invalid" className="d-block">{error}</Form.Control.Feedback>}
    </Form.Group>
  );
}

export default DynamicField;
